import { useState, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';

interface UserCache {
    [uid: string]: string | null;
}

/**
 * Custom hook for fetching and caching user display names
 * 
 * This hook provides functions to fetch user names from Firebase and caches them
 * to avoid redundant database calls. It supports both individual lookups and bulk loading.
 * 
 * @returns Object containing:
 *  - userNames: Record of uid -> display name
 *  - getUserName: Function to get a single user's name (async)
 *  - loadUserNames: Function to load multiple users' names (async)
 *  - loadAllUsers: Function to load the entire user list (async)
 */
export const useUserNames = () => {
    const [userNames, setUserNames] = useState<UserCache>({});
    const db = getDatabase();

    /**
     * Get a single user's display name
     * Checks cache first, then fetches from database if not cached
     * 
     * @param uid - User ID to fetch
     * @returns Promise resolving to the user's display name or 'Okänd användare'
     */
    const getUserName = useCallback(async (uid: string | undefined): Promise<string> => {
        if (!uid) return 'Okänd användare';

        // Return cached value if available
        if (userNames[uid] !== undefined) {
            return userNames[uid] || 'Okänd användare';
        }

        try {
            const userRef = ref(db, `users/userList/${uid}/name`);
            const userSnapshot = await get(userRef);
            
            if (userSnapshot.exists()) {
                const name = userSnapshot.val();
                setUserNames(prev => ({
                    ...prev,
                    [uid]: name
                }));
                return name || 'Okänd användare';
            }

            // User not found
            setUserNames(prev => ({
                ...prev,
                [uid]: null
            }));
            return 'Okänd användare';
        } catch (error) {
            console.error('Error fetching user name:', error);
            setUserNames(prev => ({
                ...prev,
                [uid]: null
            }));
            return 'Okänd användare';
        }
    }, [db, userNames]);

    /**
     * Load multiple users' names at once
     * More efficient than calling getUserName multiple times
     * 
     * @param uids - Array of user IDs to fetch
     */
    const loadUserNames = useCallback(async (uids: string[]): Promise<void> => {
        try {
            const names: UserCache = { ...userNames };
            
            for (const uid of uids) {
                // Skip if already cached
                if (names[uid] !== undefined) continue;
                
                const userRef = ref(db, `users/userList/${uid}/name`);
                const userSnapshot = await get(userRef);
                
                if (userSnapshot.exists()) {
                    names[uid] = userSnapshot.val();
                } else {
                    names[uid] = null;
                }
            }
            
            setUserNames(names);
        } catch (error) {
            console.error('Error loading user names:', error);
        }
    }, [db, userNames]);

    /**
     * Load the entire user list from users/userList
     * Most efficient when you need to display many users
     */
    const loadAllUsers = useCallback(async (): Promise<void> => {
        try {
            const usersRef = ref(db, 'users/userList');
            const usersSnapshot = await get(usersRef);
            
            if (usersSnapshot.exists()) {
                const usersData = usersSnapshot.val() as Record<string, { name?: string }>;
                const names: Record<string, string | null> = {};
                
                Object.entries(usersData).forEach(([userId, userData]) => {
                    names[userId] = userData?.name || null;
                });
                
                setUserNames(prev => ({
                    ...prev,
                    ...names
                }));
            }
        } catch (error) {
            console.error('Error loading all users:', error);
        }
    }, [db]);

    return {
        userNames,
        getUserName,
        loadUserNames,
        loadAllUsers
    };
};

